/**
 * Configuração e utilitários do Supabase
 * @module supabase
 */

import { createClient } from '@supabase/supabase-js';
import { Database } from './database.types';

// Variáveis de ambiente necessárias para conexão
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validação das variáveis de ambiente
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Variáveis de ambiente do Supabase não encontradas. Verifique o arquivo .env');
}

/**
 * Cliente Supabase configurado com retry e timeout
 * @constant
 */
export const supabase = createClient<Database>(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      flowType: 'pkce'
    },
    db: {
      schema: 'public'
    },
    global: {
      headers: { 'x-application-name': 'sdt-bsb' }
    },
    // Add fetch options with timeout
    fetch: (url, options) => {
      const fetchOptions = {
        ...options,
        // Set timeout to 15 seconds
        signal: AbortSignal.timeout(15000)
      };
      return fetch(url, fetchOptions);
    }
  }
);

/**
 * Verifica a conexão com o Supabase
 * @returns {Promise<boolean>} true se conectado, false caso contrário
 */
export async function checkSupabaseConnection() {
  try {
    const { data, error } = await supabase.from('small_groups').select('count').single();
    if (error) throw error;
    console.log('✅ Conexão com Supabase estabelecida');
    return true;
  } catch {
    console.error('❌ Erro na conexão com Supabase');
    return false;
  }
}

/**
 * Verifica o estado da autenticação do usuário
 * @returns {Promise<Session|null>} Sessão do usuário se autenticado, null caso contrário
 */
export async function checkAuthState() {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    console.log('✅ Estado de autenticação:', session ? 'Autenticado' : 'Não autenticado');
    return session;
  } catch (error) {
    console.error('❌ Erro ao verificar autenticação:', error);
    return null;
  }
}

/**
 * Realiza o cadastro de um novo usuário
 * @param {string} email - Email do usuário
 * @param {string} password - Senha do usuário
 * @param {string} name - Nome do usuário
 * @returns {Promise<AuthResponse>} Resposta da autenticação
 */
export async function signUp(email: string, password: string, name: string) {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { name }
      }
    });
    
    if (error) throw error;
    console.log('✅ Usuário cadastrado com sucesso');
    return data;
  } catch (error) {
    console.error('❌ Erro ao cadastrar usuário:', error);
    throw error;
  }
}

/**
 * Realiza o login de um usuário
 * @param {string} email - Email do usuário
 * @param {string} password - Senha do usuário
 * @returns {Promise<AuthResponse>} Resposta da autenticação
 */
export async function signIn(email: string, password: string) {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (error) throw error;
    console.log('✅ Login realizado com sucesso');
    return data;
  } catch (error) {
    console.error('❌ Erro ao fazer login:', error);
    throw error;
  }
}

/**
 * Realiza o logout do usuário
 * @returns {Promise<void>}
 */
export async function signOut() {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    console.log('✅ Logout realizado com sucesso');
  } catch (error) {
    console.error('❌ Erro ao fazer logout:', error);
    throw error;
  }
}

/**
 * Recupera a senha do usuário
 * @param {string} email - Email do usuário
 * @returns {Promise<void>}
 */
export async function resetPassword(email: string) {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
      captchaToken: undefined
    });
    
    if (error) throw error;
    console.log('✅ Email de recuperação enviado com sucesso');
  } catch (error) {
    console.error('❌ Erro ao enviar email de recuperação:', error);
    throw error;
  }
}

/**
 * Atualiza os dados do usuário
 * @param {object} userData - Dados do usuário a serem atualizados
 * @returns {Promise<User>} Dados atualizados do usuário
 */
export async function updateUserProfile(userData: { name?: string, avatar_url?: string }) {
  try {
    const { data, error } = await supabase.auth.updateUser({
      data: userData
    });
    
    if (error) throw error;
    console.log('✅ Perfil atualizado com sucesso');
    return data.user;
  } catch (error) {
    console.error('❌ Erro ao atualizar perfil:', error);
    throw error;
  }
}

/**
 * Obtém os dados do usuário atual
 * @returns {Promise<User|null>} Dados do usuário ou null se não estiver autenticado
 */
export async function getCurrentUser() {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  } catch (error) {
    console.error('❌ Erro ao obter usuário atual:', error);
    return null;
  }
}

/**
 * Obtém a igreja do usuário atual
 * @returns {Promise<any|null>} Dados da igreja ou null se não estiver definida
 */
export async function getUserChurch() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    // Buscar o usuário no banco de dados para obter a igreja_id
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('igreja_id')
      .eq('id', user.id)
      .single();

    if (userError) throw userError;
    if (!userData?.igreja_id) return null;

    // Buscar os dados da igreja
    const { data: churchData, error: churchError } = await supabase
      .from('igrejas')
      .select('*')
      .eq('id', userData.igreja_id)
      .single();

    if (churchError) throw churchError;
    return churchData;
  } catch (error) {
    console.error('❌ Erro ao obter igreja do usuário:', error);
    return null;
  }
}

/**
 * Registra uma ação do usuário para análise
 * @param {string} action - Ação realizada
 * @param {object} details - Detalhes da ação
 * @returns {Promise<boolean>} true se registrado com sucesso, false caso contrário
 */
export async function logUserAction(action: string, details: any = {}) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { error } = await supabase
      .from('user_actions')
      .insert([{
        user_id: user.id,
        action,
        details,
        ip_address: '0.0.0.0', // Em um ambiente real, você capturaria o IP
        user_agent: navigator.userAgent
      }]);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('❌ Erro ao registrar ação do usuário:', error);
    return false;
  }
}